import * as React from "react";
import dayjs from "dayjs";
import { createPortal } from "react-dom";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { CPText } from "../../base";
import { Ripple } from "@progress/kendo-react-ripple";
import {
  AppBar,
  AppBarSection,
  AppBarSpacer,
  Drawer,
  DrawerContent,
  DrawerItem,
  DrawerItemProps,
  DrawerSelectEvent,
} from "@progress/kendo-react-layout";
import {
  Switch,
  SwitchChangeEvent,
  TextBox,
} from "@progress/kendo-react-inputs";
import CPLoader from "../../shared/CPLoader/CPLoader";
import useAppStore from "../../../state/useAppState";
import { getAppState, updateAppState } from "../../../appStateStorage";
import { accessTokenName } from "shared/config";
import { getValueFromCookie, setSessionExpiredToastFlag } from "shared/helpers";
import "./CPLoggedInLayoutStyles.scss";
import CPConfirmModal from "../../shared/CPConfirmModal/CPConfirmModal";
import {
  CategoryResource,
  getNameInitials,
  useCurrentUserQuery,
} from "data/user/user";
import { useLogout } from "data/auth/auth";
import AlertSettingsModal from "./AlertSettingsModal";
import BuildUpdateBanner from "../BuildUpdateBanner/BuildUpdateBanner";

import logowhite from "../../../../assets/evans-bi-logo4-white.png";

const BRAND_CLASS = "cp-nav-brand-item";

interface NavCategory {
  categoryId: number;
  name: string;
  url: string;
  icon: string;
  children: { name: string; url: string }[];
  resources: {
    resourceId: number;
    name: string;
    resourceType: string;
    url?: string | null;
  }[];
}

const resourceTypeIcon = (resourceType: string): string => {
  switch (resourceType?.toUpperCase()) {
    case "REPORT":
      return "fa-solid fa-chart-column";
    case "FILE":
      return "fa-solid fa-file-arrow-down";
    case "PAGE":
      return "fa-solid fa-file-lines";
    case "URL":
      return "fa-solid fa-link";
    default:
      return "fa-solid fa-chart-column";
  }
};

interface CPLoggedInLayoutProps {
  isTestSite?: boolean;
  useAdminLayout?: boolean;
  onToggleLayout?: (value: boolean) => void;
}

const toCategoryUrl = (name: string) => `/category/${name.replace(/ /g, "-")}`;

// Pool of BI-related icons cycled by index so every category looks distinct
const LEAF_ICONS = [
  "fa-solid fa-chart-column",
  "fa-solid fa-chart-line",
  "fa-solid fa-chart-pie",
  "fa-solid fa-chart-area",
  "fa-solid fa-chart-bar",
  "fa-solid fa-table-columns",
];
const PARENT_ICONS = [
  "fa-solid fa-layer-group",
  "fa-solid fa-sitemap",
  "fa-solid fa-diagram-project",
  "fa-solid fa-folder-tree",
];

// Derive a short acronym for the mini (collapsed) label
const toMiniLabel = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return name.substring(0, 4).toUpperCase();
  return words
    .map((w) => w[0])
    .join("")
    .substring(0, 4)
    .toUpperCase();
};

const buildCategoryMenus = (
  categoryResources: CategoryResource[],
): NavCategory[] => {
  const topLevel = categoryResources.filter((c) => c.parentCategory === null);
  const childrenMap: Record<string, CategoryResource[]> = {};
  categoryResources
    .filter((c) => c.parentCategory !== null)
    .forEach((c) => {
      const parent = c.parentCategory!;
      if (!childrenMap[parent]) childrenMap[parent] = [];
      childrenMap[parent].push(c);
    });

  return topLevel.map((cat, idx) => {
    const childCats = childrenMap[cat.name] ?? [];
    const childResources = childCats.flatMap((child) => child.resources);
    const seenIds = new Set<number>();
    const allResources = [...cat.resources, ...childResources].filter((r) => {
      if (seenIds.has(r.resourceId)) return false;
      seenIds.add(r.resourceId);
      return true;
    });
    return {
      categoryId: cat.categoryId,
      name: cat.name,
      url: toCategoryUrl(cat.name),
      icon:
        childCats.length > 0
          ? PARENT_ICONS[idx % PARENT_ICONS.length]
          : LEAF_ICONS[idx % LEAF_ICONS.length],
      children: childCats.map((child) => ({
        name: child.name,
        url: toCategoryUrl(child.name),
      })),
      resources: allResources.map((r) => ({
        resourceId: r.resourceId,
        name: r.name,
        resourceType: r.resourceType,
        url: r.url,
      })),
    };
  });
};

const CPLoggedInLayout: React.FC<CPLoggedInLayoutProps> = ({
  isTestSite = false,
  useAdminLayout = false,
  onToggleLayout,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isLoading = useAppStore((app) => app.isLoading);
  const setIsLoading = useAppStore((app) => app.setIsLoading);
  const [searchValue, setSearchValue] = React.useState("");

  // Sync the search input with the ?q= param when on the search page
  React.useEffect(() => {
    if (location.pathname === "/search") {
      const params = new URLSearchParams(location.search);
      setSearchValue(params.get("q") ?? "");
    } else {
      setSearchValue("");
    }
  }, [location.pathname, location.search]);
  const [expanded, setExpanded] = React.useState(false);

  React.useEffect(() => {
    getAppState().then((s) => {
      if (s.userDrawerExpanded !== undefined) {
        setExpanded(s.userDrawerExpanded);
      }
    });
  }, []);

  const handleToggleDrawer = React.useCallback(() => {
    setExpanded((v) => {
      const next = !v;
      updateAppState({ userDrawerExpanded: next });
      return next;
    });
  }, []);

  const [popupMenu, setPopupMenu] = React.useState<{
    name: string;
    top: number;
  } | null>(null);

  const { data: user } = useCurrentUserQuery();

  const nameInitials = React.useMemo(() => {
    if (!user) return "";
    return getNameInitials(user);
  }, [user]);

  // Close popup when sidebar opens
  React.useEffect(() => {
    if (expanded) setPopupMenu(null);
  }, [expanded]);

  // Close popup on outside click
  React.useEffect(() => {
    if (!popupMenu) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest(".cp-mini-popup")) {
        setPopupMenu(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [popupMenu]);

  const expandedStateRef = React.useRef<Record<string, boolean>>({});
  const submenuRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const chevronRefs = React.useRef<Record<string, HTMLSpanElement | null>>({});

  const toggleMenu = React.useCallback((menuName: string) => {
    const next = !(expandedStateRef.current[menuName] ?? false);
    expandedStateRef.current = {
      ...expandedStateRef.current,
      [menuName]: next,
    };
    submenuRefs.current[menuName]?.classList.toggle(
      "cp-nav-submenu-open",
      next,
    );
    chevronRefs.current[menuName]?.classList.toggle(
      "cp-nav-chevron-open",
      next,
    );
  }, []);

  const categoryMenus = React.useMemo(
    () => buildCategoryMenus(user?.categoryResources ?? []),
    [user?.categoryResources],
  );

  const isAgencyManager = React.useMemo(() => {
    const roles = user?.roles ?? [];
    return (
      roles.includes("Agency Manager") &&
      !roles.some((r) => ["Admin", "Site Manager", "Employee"].includes(r))
    );
  }, [user?.roles]);

  const isAdmin = React.useMemo(() => {
    return (
      user?.roles?.includes("Admin") ||
      user?.roles?.includes("Site Manager") ||
      false
    );
  }, [user?.roles]);

  const canManageAlerts = React.useMemo(() => {
    //"Agency Manager" not enabled right now...
    return (
      user?.roles?.some((r) =>
        ["Admin", "Employee", "Site Manager"].includes(r),
      ) ?? false
    );
  }, [user?.roles]);

  const [userMenuAnchor, setUserMenuAnchor] = React.useState<{
    top: number;
    right: number;
  } | null>(null);
  const [alertModalOpen, setAlertModalOpen] = React.useState(false);
  const avatarRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!userMenuAnchor) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest(".cp-user-dropdown")) {
        setUserMenuAnchor(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [userMenuAnchor]);

  const drawerItems = React.useMemo(() => {
    const brandItem = {
      text: "BI HUB",
      icon: "fa-solid fa-bolt",
      route: "",
      selected: false,
      disabled: true,
      className: BRAND_CLASS,
      hasChildren: false,
      isChild: false,
    };

    const dashboardItem = {
      text: "Dashboard",
      icon: "fa-solid fa-home",
      route: "/",
      selected: false,
      disabled: false,
      hasChildren: false,
      isChild: false,
    };

    const navItems = categoryMenus.map((cat) => ({
      text: cat.name,
      icon: cat.icon,
      miniLabel: toMiniLabel(cat.name),
      route: cat.url,
      categoryId: cat.categoryId,
      selected: false,
      disabled: false,
      hasChildren: cat.children.length > 0 || cat.resources.length > 0,
      isChild: false,
    }));

    const logoutItem = {
      text: "Logout",
      icon: "fa-solid fa-arrow-right-from-bracket",
      route: "/Logout",
      selected: false,
      disabled: false,
      hasChildren: false,
      isChild: false,
    };

    const items = [brandItem, dashboardItem, ...navItems];
    items.push(logoutItem);

    return items;
  }, [categoryMenus]);

  const logout = useLogout();

  const isTokenExpired = React.useCallback(() => {
    const expires = getValueFromCookie(accessTokenName, "expires");
    if (!expires) return true;
    const expiresAt = dayjs(expires);
    return !expiresAt.isValid() || expiresAt.isBefore(dayjs());
  }, []);

  React.useEffect(() => {
    const checkTokenAndLogout = () => {
      if (isTokenExpired()) {
        setSessionExpiredToastFlag();
        logout();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkTokenAndLogout();
      }
    };

    window.addEventListener("focus", checkTokenAndLogout);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", checkTokenAndLogout);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [logout, isTokenExpired]);

  const handleSelect = (e: DrawerSelectEvent) => {
    const itemProps = e.itemTarget.props as any;
    if (itemProps.hasChildren) return;
    const route = itemProps.route as string | undefined;
    if (route === "/Logout") {
      logout();
      return;
    }
    if (route) navigate(route);
  };

  const renderItem = React.useCallback(
    (props: DrawerItemProps) => {
      const isBrand = (props.className as string | undefined)?.includes(
        BRAND_CLASS,
      );

      if (isBrand) {
        return (
          <DrawerItem {...props}>
            <div className="cp-nav-brand-inner">
              <span
                className="cp-brand-logo"
                style={{ backgroundImage: `url(${logowhite})` }}
                role="img"
                aria-label="BI HUB Logo"
              />
              <span className="cp-brand-text">BI HUB</span>
              {isTestSite && <span className="cp-brand-test-badge">TEST</span>}
            </div>
          </DrawerItem>
        );
      }

      const hasChildren = (props as any).hasChildren as boolean;
      const menuName = props.text as string;
      const isMenuExpanded = expandedStateRef.current[menuName] ?? false;
      const menuEntry = categoryMenus.find((m) => m.name === menuName) ?? null;

      const route = (props as any).route as string | undefined;
      const isSelected = !!(
        route &&
        !props.disabled &&
        window.location.pathname === route
      );

      return (
        <DrawerItem {...props} selected={isSelected} title={menuName}>
          <div
            className="cp-nav-item-inner"
            onClick={
              hasChildren
                ? (e) => {
                    e.stopPropagation();
                    if (!expanded) {
                      const rect = (
                        e.currentTarget as HTMLElement
                      ).getBoundingClientRect();
                      setPopupMenu((prev) =>
                        prev?.name === menuName
                          ? null
                          : { name: menuName, top: rect.top },
                      );
                    } else {
                      toggleMenu(menuName);
                    }
                  }
                : undefined
            }
          >
            <span className="cp-nav-item-icon-wrap">
              <i className={props.icon ?? ""} />
              {(props as any).miniLabel && (
                <span className="cp-nav-mini-label">
                  {(props as any).miniLabel}
                </span>
              )}
            </span>
            <span className="cp-nav-item-label">{menuName}</span>
            {hasChildren && (
              <span
                className={`cp-nav-chevron${isMenuExpanded ? " cp-nav-chevron-open" : ""}`}
                ref={(el: HTMLSpanElement | null) => {
                  chevronRefs.current[menuName] = el;
                }}
                onClick={(e) => {
                  if (expanded) {
                    e.stopPropagation();
                    toggleMenu(menuName);
                  }
                }}
              >
                <i className="fa-solid fa-chevron-down" />
              </span>
            )}
          </div>
          {menuEntry && expanded && (
            <div
              className={`cp-nav-submenu${isMenuExpanded ? " cp-nav-submenu-open" : ""}`}
              ref={(el: HTMLDivElement | null) => {
                submenuRefs.current[menuName] = el;
              }}
            >
              {menuEntry.resources.map((res) => (
                <div
                  key={res.resourceId}
                  className="cp-nav-child-row"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (res.resourceType === "REPORT") {
                      navigate(
                        `/category/${menuEntry.categoryId}/report/${res.resourceId}`,
                      );
                    } else if (res.resourceType === "PAGE" && res.url) {
                      navigate(res.url);
                    } else {
                      navigate(menuEntry.url);
                    }
                  }}
                >
                  <i
                    className={`${resourceTypeIcon(res.resourceType)} cp-nav-child-res-icon`}
                  />
                  <span className="cp-nav-child-label">{res.name}</span>
                </div>
              ))}
            </div>
          )}
        </DrawerItem>
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [expanded, isTestSite, toggleMenu, navigate, setPopupMenu, categoryMenus],
  );

  const drawerClass = [
    "cp-loggedin-drawer",
    !expanded ? "cp-drawer-collapsed" : "",
    isTestSite ? "cp-layout-test" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Ripple>
      <CPLoader isLoading={isLoading} />

      <Drawer
        className={drawerClass}
        expanded={expanded}
        position="start"
        mode="push"
        mini={true}
        miniWidth={64}
        width={240}
        items={drawerItems}
        item={renderItem}
        onSelect={handleSelect}
      >
        <DrawerContent className="cp-loggedin-drawer-content">
          {/* Top app bar */}
          <AppBar className="cp-top-appbar" positionMode="static">
            <AppBarSection>
              <button
                className="cp-hamburger-btn"
                onClick={handleToggleDrawer}
                title="Toggle navigation"
              >
                <i className="fa-solid fa-bars" />
              </button>
            </AppBarSection>

            <AppBarSection className="cp-search-section">
              <i className="fa-solid fa-magnifying-glass cp-search-icon" />
              <TextBox
                className="cp-search-input"
                placeholder="Search categories or reports..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.value as string)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && searchValue.trim().length >= 2) {
                    setIsLoading(true);
                    navigate(
                      `/search?q=${encodeURIComponent(searchValue.trim())}`,
                    );
                  }
                }}
              />
            </AppBarSection>

            <AppBarSpacer />

            {/* {isAgencyManager && !isAdmin && (
              <AppBarSection>
                <button
                  className="cp-my-agency-btn"
                  title="My Agency"
                  onClick={() => navigate("/agency-management/users")}
                >
                  <i className="fa-solid fa-building" />
                  <span>My Agency</span>
                </button>
              </AppBarSection>
            )} */}

            {isTestSite && (
              <AppBarSection>
                <span className="cp-topbar-test-badge">TEST</span>
              </AppBarSection>
            )}

            {onToggleLayout && (
              <AppBarSection className="cp-layout-toggle-section">
                <span className="cp-layout-toggle-label">Admin View</span>
                <Switch
                  checked={useAdminLayout}
                  onChange={(e: SwitchChangeEvent) => onToggleLayout(e.value)}
                  size="small"
                />
              </AppBarSection>
            )}

            {canManageAlerts && (
              <AppBarSection>
                <button
                  className="cp-alert-bell-btn"
                  title="Alert Settings"
                  onClick={() => setAlertModalOpen(true)}
                >
                  <i className="fa-solid fa-bell" />
                </button>
              </AppBarSection>
            )}

            <AppBarSection style={{ position: "relative" }}>
              <div
                ref={avatarRef}
                className="cp-user-avatar-btn"
                title={user?.firstname + " " + user?.lastname}
                onClick={() => {
                  if (userMenuAnchor) {
                    setUserMenuAnchor(null);
                    return;
                  }
                  const rect = avatarRef.current?.getBoundingClientRect();
                  if (rect) {
                    setUserMenuAnchor({
                      top: rect.bottom + 8,
                      right: window.innerWidth - rect.right,
                    });
                  }
                }}
              >
                <CPText font="montserrat" className="cp-user-initials">
                  {nameInitials}
                </CPText>
              </div>
            </AppBarSection>
          </AppBar>

          {/* Page content */}
          <div className="cp-page-content">
            <Outlet />
          </div>
        </DrawerContent>
      </Drawer>

      <CPConfirmModal />

      <AlertSettingsModal
        open={alertModalOpen}
        onClose={() => setAlertModalOpen(false)}
        user={user}
      />

      {popupMenu &&
        createPortal(
          <div
            className="cp-mini-popup cp-loggedin-popup"
            style={{ top: popupMenu.top }}
          >
            {(() => {
              const menuEntry = categoryMenus.find(
                (m) => m.name === popupMenu.name,
              );
              if (!menuEntry) return null;
              return (
                <>
                  <div className="cp-mini-popup-title">{menuEntry.name}</div>
                  {menuEntry.resources.map((res) => (
                    <div
                      key={res.resourceId}
                      className="cp-mini-popup-item"
                      onClick={() => {
                        setPopupMenu(null);
                        if (res.resourceType === "REPORT") {
                          navigate(
                            `/category/${menuEntry.categoryId}/report/${res.resourceId}`,
                          );
                        } else if (res.resourceType === "PAGE" && res.url) {
                          navigate(res.url);
                        } else {
                          navigate(menuEntry.url);
                        }
                      }}
                    >
                      <i className={resourceTypeIcon(res.resourceType)} />
                      <span>{res.name}</span>
                    </div>
                  ))}
                </>
              );
            })()}
          </div>,
          document.body,
        )}
      {userMenuAnchor &&
        createPortal(
          <div
            className="cp-user-dropdown"
            style={{ top: userMenuAnchor.top, right: userMenuAnchor.right }}
          >
            <div className="cp-user-dropdown-header">
              <span className="cp-user-dropdown-name">
                {user?.firstname} {user?.lastname}
              </span>
              <span className="cp-user-dropdown-email">{user?.email}</span>
            </div>
            <div className="cp-user-dropdown-divider" />
            <div
              className="cp-user-dropdown-item"
              onClick={() => {
                setUserMenuAnchor(null);
                navigate("/MyProfile");
              }}
            >
              <i className="fa-solid fa-user" />
              <span>My Profile</span>
            </div>
            <div className="cp-user-dropdown-divider" />
            <div
              className="cp-user-dropdown-item"
              title="Open a ticket with the help desk"
              onClick={() => {
                setUserMenuAnchor(null);
                const supportBody = [
                  "Please answer the following questions to help us resolve your issue quickly:",
                  "",
                  "1. What is the issue you are experiencing?",
                  "   [Describe the issue here]",
                  "",
                  "2. What were you doing when the issue occurred?",
                  "   [Describe your steps leading up to the issue]",
                  "",
                  "3. Does this issue prevent you from completing your work? (Yes / No)",
                  "   ",
                  "",
                  "4. How urgent is this issue? (Low / Medium / High / Critical)",
                  "   ",
                  "",
                  "5. How long has this been occurring? (Just started / Today / Several days / Ongoing)",
                  "   ",
                  "",
                  "6. Steps to reproduce the issue (if known):",
                  "   1. ",
                  "   2. ",
                  "   3. ",
                  "",
                  "7. Are there any error messages displayed? If so, please paste them below:",
                  "   [Paste any error messages here]",
                  "",
                  "8. Browser and device (e.g. Chrome on Windows 10):",
                  "   ",
                ].join("\n");
                window.location.href = `mailto:tech_deck@evansdelivery.com?subject=Evans%20BiHub%20-%20Support%20Requested&body=${encodeURIComponent(supportBody)}`;
              }}
            >
              <i className="fa-solid fa-headset" />
              <span>Contact Support</span>
            </div>
            <div className="cp-user-dropdown-divider" />
            <div
              className="cp-user-dropdown-item"
              onClick={() => {
                setUserMenuAnchor(null);
                logout();
              }}
            >
              <i className="fa-solid fa-arrow-right-from-bracket" />
              <span>Log Out</span>
            </div>
          </div>,
          document.body,
        )}
      <BuildUpdateBanner />
    </Ripple>
  );
};

export default CPLoggedInLayout;
